import { Injectable } from '@angular/core';
import { Item, Product } from '../../models/';

import { ApiClient } from '../api.client';
import { Observable } from 'rxjs';
import {
  PriceSuggestion,
  ProductHireEstimateRequest
} from '../../models/product-hire-estimate';
import { Price } from '../../models/price';
import { currencyStringToNumber } from '../helpers/currencyStringToNumber';
import { map } from 'rxjs/operators';
import { LocalProduct } from 'src/app/products/shopfront-importer/import-product';
export interface PriceStructure {
  currency: string;
  first_day: number;
  add_day: number;
  week: number;
  weekend: number;
  month: number;
}
@Injectable()
export class ProductService {
  constructor(private http: ApiClient) {}

  public getProducts(include = ['items'], appends: string[] = []) {
    let qs =
      this.http.getWiths(include) +
      '&' +
      this.http.urlSerializeArray(appends, 'attributes');
    return this.http.get<Product[]>(`products${qs}`);
  }

  public getProduct(
    id: number,
    include: string[] = [],
    appends: string[] = [],
    select: Array<keyof Product> = []
  ) {
    const qs = [
      this.http.getWiths(include),
      this.http.urlSerializeArray(appends, 'attributes'),
      this.http.urlSerializeArray(select, 'select')
    ].join('&');
    return this.http.get<Product | Partial<Product>>(`products/` + id + qs);
  }

  public getProductsById<T extends Partial<Product>>(
    ids: number[],
    include: string[] = [],
    appends: string[] = [],
    select: Array<keyof Product> = []
  ) {
    if (ids.length === 1) {
      return this.getProduct(ids[0], include, appends, select).pipe(
        map((result) => [result])
      );
    }
    const qs = [
      this.http.getWiths(include),
      this.http.urlSerializeArray(appends, 'attributes'),
      this.http.urlSerializeArray(select, 'select')
    ].join('&');
    return this.http.get<Product[] | Partial<Product>[] | T[]>(
      `products/` + encodeURIComponent(ids.join(',')) + qs
    );
  }

  public addProduct(product: Product) {
    product.default_prices = this.convertPricesToPennies(
      product.default_prices
    );
    if (product.replacement_price && isNaN(product.replacement_price)) {
      product.replacement_price = currencyStringToNumber(
        <any>product.replacement_price
      );
    }
    return this.http.post<Product>(`products`, product);
  }

  public updateProduct(id: number, product: Partial<Product>) {
    if (product.default_prices) {
      product.default_prices = this.convertPricesToPennies(
        product.default_prices
      );
    }
    if (product.replacement_price && isNaN(product.replacement_price)) {
      product.replacement_price = currencyStringToNumber(
        <any>product.replacement_price
      );
    }
    return this.http.put(`products/` + id, product);
  }

  public getUncategorised(): Observable<Product[]> {
    return this.http.get('products/uncategorised');
  }

  delete(productId: number) {
    return this.http.delete(`products/${productId}`);
  }

  searchProductsAndItems(search: string): Observable<Product[]> {
    return this.http.get(
      `products/hireItemSearch/${encodeURIComponent(search)}`
    );
  }

  search(search: string): Observable<Product[]> {
    return this.http.get<Product[]>(
      `products/search/${encodeURIComponent(search)}`
    );
  }

  uploadImage(image: File, productId: number) {
    const formData = new FormData();
    formData.append('image', image);
    return this.http.post(`products/${productId}/image`, formData);
  }

  deleteImage(productId: number) {
    return this.http.delete(`products/${productId}/image`);
  }

  getPrice(products: ProductHireEstimateRequest[]) {
    return this.http.post<PriceSuggestion[]>(
      `products/hire-price-estimate`,
      products
    );
  }

  addCustomPrice(payload) {
    return this.http.post<void>(`products/custom-price`, payload);
  }

  deleteCustomPrice(
    productId: number,
    customerId: number,
    hireableId?: number,
    hireableType?: string
  ) {
    const qs = new URLSearchParams();
    if (hireableType) {
      qs.set('hireable_type', hireableType);
    }
    if (hireableId) {
      qs.set('hireable_id', hireableId.toString());
    }
    if (customerId) {
      qs.set('customer_id', customerId.toString());
    }
    if (productId) {
      qs.set('product_id', productId.toString());
    }
    return this.http.delete<void>(`products/custom-price?${qs.toString()}`);
  }

  getRelatedItems(productId: number) {
    return this.http.get<Item[]>(`products/${productId}/related-items`);
  }

  private convertPricesToPennies(prices: Price[]) {
    return prices
      .filter((price) => !!price.value)
      .map((price) => {
        price.value = price.value
          ? currencyStringToNumber(<any>price.value) * 100
          : null;
        return price;
      });
  }

  getConfiguredPrices(
    productId: number,
    customer_id: number | null,
    currency: string | null,
    hireableId?: number,
    hireableType?: string
  ) {
    let query = '?';
    if (customer_id) {
      query += `customer_id=${customer_id}&`;
    }
    if (currency) {
      query += `currency=${currency}`;
    }
    if (hireableId) {
      query += `&hireable_id=${hireableId}`;
    }
    if (hireableType) {
      query += `&hireable_type=${hireableType}`;
    }
    return this.http.get<PriceStructure>(
      `products/${productId}/configured-prices${query}`
    );
  }

  importShopfrontProduct(productId: number, product: LocalProduct) {
    return this.http.post(`products/${productId}/import-shopfront`, product);
  }

  getLinkedToProducts(productId: number) {
    return this.http.get<Product[]>(`products/${productId}/linked-to`);
  }

  exportProductList(): Observable<Blob> {
    return this.http.get(`products/list`, {
      headers: {
        Accept:
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      },
      responseType: 'blob'
    });
  }
}
